import { z } from 'zod';
import { FileInfo, MAX_FILES_PER_MESSAGE } from './files.js';
import { GIPHY_ID, Gif } from './gifs.js';
import { ChapiMessage } from './chapi.js';
import { ChapiChatReply } from './chapi-chat.js';
import { Notification } from './notifications.js';
import { MyAvailability, PersonAvailability } from './notification-prefs.js';
import { PersonRef } from './people.js';
import { RepoActivity } from './repos.js';
import { UserStatus } from './status.js';
import { TimeMentions } from './time-mentions.js';
import { CatchUp, CatchUpSignal, ConnId } from './catch-ups.js';
import { Priority, WorkItemType } from './work-items.js';
export const ChannelKind = z.enum(['client', 'project', 'team', 'dm']);
export const ChannelVisibility = z.enum(['public', 'private', 'dm']);
export const ChannelSummary = z.object({
    id: z.uuid(),
    kind: ChannelKind,
    name: z.string(),
    visibility: ChannelVisibility,
    clientId: z.uuid().nullable(),
    projectId: z.uuid().nullable(),
    unread: z.number().int(),
    mentions: z.number().int(),
    /** The signed-in person's notification level here ("all" when they have not joined). */
    notify: z.enum(['all', 'mentions', 'none']),
    /** For DMs: the other person. */
    dmWith: PersonRef.nullable(),
    online: z.boolean().optional(),
    /** Chapi is switched off here by an owner or admin: it never reads this channel, and the apps hide its shortcuts. */
    chapiOff: z.boolean(),
    /** When the last message was posted, for the DMs list (newest first). Absent from older servers. */
    lastMessageAt: z.string().nullable().optional(),
    /**
     * Conversation types (docs/adr/0085). A group chat is a `dm` channel with more than two people; its `name` is its own
     * or made from its people. Absent from older servers.
     */
    groupChat: z.boolean().optional(),
    /** People from outside the workspace are in it right now: the shared-channel icon (UI-08). */
    external: z.boolean().optional(),
    /** A channel or group chat filed under this client (or this project's client) in the panel. Never changes access. */
    linkClientId: z.uuid().nullable().optional(),
    linkProjectId: z.uuid().nullable().optional(),
    /** In the signed-in person's Starred section. */
    starred: z.boolean().optional(),
    /**
     * The chat list's second line (docs/adr/0087, Mobile-v4-03): the latest person's message the signed-in person may read
     * here, as short plain text (formatting dropped, mentions as names, at most CHANNEL_PREVIEW_MAX characters). Never a
     * system line, a deleted message or one from someone they blocked; for a conversation guest never the team's activity.
     * Null when there is none; absent from older servers and outside the sidebar.
     */
    preview: z
        .object({
        authorId: z.uuid().nullable(),
        /** The author's name (or an imported author's); null when unknown. */
        authorName: z.string().nullable(),
        text: z.string().max(120),
        at: z.string(),
    })
        .nullable()
        .optional(),
});
/** The longest preview text the sidebar sends (docs/adr/0087). */
export const CHANNEL_PREVIEW_MAX = 120;
export const Sidebar = z.object({
    clients: z.array(z.object({ clientId: z.uuid(), name: z.string(), channels: z.array(ChannelSummary) })),
    team: z.array(ChannelSummary),
    dms: z.array(ChannelSummary),
    /** Group chats not filed under a client (docs/adr/0085). Absent from older servers. */
    groups: z.array(ChannelSummary).optional(),
    /** The signed-in person's starred conversations, in their order; these are left out of the other lists. */
    starred: z.array(ChannelSummary).optional(),
    inboxCount: z.number().int(),
});
export const ProjectTag = z.object({ id: z.uuid(), name: z.string(), key: z.string() });
export const Pin = z.object({ id: z.uuid(), label: z.string(), messageId: z.uuid().nullable() });
export const ChannelDetail = z.object({
    channel: ChannelSummary,
    topic: z.string(),
    clientName: z.string().nullable(),
    projectName: z.string().nullable(),
    projectStatus: z.string().nullable(),
    subtitle: z.string(),
    projects: z.array(ProjectTag),
    members: z.array(PersonRef),
    memberCount: z.number().int(),
    /**
     * Members who are client guests. With any, or in a client channel, staff composers check a draft for rude words,
     * secrets and internal links before it posts (docs/adr/0066-client-post-check.md).
     */
    guestCount: z.number().int().min(0),
    pins: z.array(Pin),
    counts: z.object({ tasks: z.number().int(), support: z.number().int() }),
    notify: z.enum(['all', 'mentions', 'none']),
    /**
     * Repositories connected to the projects whose activity posts here (docs/adr/0052), for staff while the feature is
     * on; null otherwise. `activity`: the channel shows a one-line entry for each push and pull request (off by default).
     */
    repo: z.object({ provider: z.string(), lastPushAt: z.string().nullable(), count: z.number().int(), activity: z.boolean() }).nullable(),
    canPost: z.boolean(),
    canManage: z.boolean(),
    /** Archived channels are read-only and hidden from the sidebar. */
    archived: z.boolean(),
    /** Conversation types (docs/adr/0085): a group chat (a `dm` channel with several people). */
    groupChat: z.boolean().optional(),
    /**
     * People from outside the workspace in it right now, with their companies (the client they were invited for, else
     * their email domain): the External chip, "6 · 2 external", the company chips and the amber bar. Null when none.
     */
    external: z
        .object({
        count: z.number().int().min(1),
        companies: z.array(z.object({ name: z.string(), count: z.number().int() })),
        people: z.array(z.object({ userId: z.uuid(), company: z.string() })),
    })
        .nullable()
        .optional(),
    /** Created External or has had outside people invited: the team may invite more (owners and admins). */
    allowExternal: z.boolean().optional(),
    /** The client or project a channel or group chat is filed under. */
    link: z.object({ clientId: z.uuid().nullable(), clientName: z.string().nullable(), projectId: z.uuid().nullable(), projectName: z.string().nullable() }).nullable().optional(),
    /** The signed-in person may invite workspace people; may invite people from outside; may remove people. */
    canInvite: z.boolean().optional(),
    canInviteExternal: z.boolean().optional(),
    canRemove: z.boolean().optional(),
    /** In the signed-in person's Starred section. */
    starred: z.boolean().optional(),
});
export const Reaction = z.object({ emoji: z.string(), count: z.number().int(), mine: z.boolean() });
/** A compact card for a work item referenced in chat (task created from a message, ticket logged). */
export const WorkItemCard = z.object({
    id: z.uuid(),
    key: z.string(),
    type: WorkItemType,
    title: z.string(),
    statusLabel: z.string(),
    statusColor: z.string(),
    priority: Priority,
    assignee: PersonRef.nullable(),
    dueDate: z.string().nullable(),
    estimateMinutes: z.number().int().nullable(),
    requesterEmail: z.string().nullable(),
});
export const MessageKind = z.enum(['user', 'system']);
/** One line of the original message, shown above a reply. Tapping it scrolls to the original. */
export const ReplyQuote = z.object({
    id: z.uuid(),
    seq: z.number().int(),
    author: PersonRef.nullable(),
    /** Plain text, mentions resolved, one line. Empty when the original was deleted. */
    excerpt: z.string(),
    deleted: z.boolean(),
});
export const Message = z.object({
    id: z.uuid(),
    attachments: z.array(FileInfo),
    /** A GIF sent with the message (GIPHY). */
    gif: Gif.nullable(),
    channelId: z.uuid(),
    seq: z.number().int(),
    kind: MessageKind,
    author: PersonRef.nullable(),
    /** Display suffix for guests, e.g. the client's name ("Dragana · Simonds"). */
    authorOrg: z.string().nullable(),
    body: z.string(),
    project: ProjectTag.nullable(),
    /** Set when this message is a reply: the quote shown above it (docs/designs/mobile/CHAT_CHANGES.md). */
    replyTo: ReplyQuote.nullable(),
    /** The first message of this reply's conversation; null on messages that are not replies. */
    rootMessageId: z.uuid().nullable(),
    /** On a conversation's first message: live replies in it, and up to three people who replied. */
    replyCount: z.number().int(),
    replyAuthors: z.array(PersonRef),
    lastReplyAt: z.string().nullable(),
    reactions: z.array(Reaction),
    /** Work item created from this message (shown as "Task created from this message"). */
    createdItem: WorkItemCard.nullable(),
    /** For system lines: what happened. */
    system: z
        .object({
        event: z.enum(['status_changed', 'ticket_logged', 'item_created', 'time_logged', 'ticket_email_in', 'ticket_replied', 'channel_created', 'repo_activity', 'catch_up_started', 'catch_up_ended', 'design_shared', 'design_comments', 'design_version', 'design_approved', 'chapi_reply', 'rule_post']),
        item: WorkItemCard.nullable(),
        actor: PersonRef.nullable(),
        from: z.string().nullable(),
        to: z.string().nullable(),
        /** Catch-up lines (docs/adr/0057): how long it ran and how many people joined (ended lines only). */
        catchUp: z.object({ id: z.uuid(), minutes: z.number().int().nullable(), people: z.number().int().nullable() }).nullable().optional(),
        /** `repo_activity` lines (docs/adr/0052): the push or pull request. Null for guests and once disconnected. */
        repo: RepoActivity.nullable().optional(),
        /** Showcase cards (docs/adr/0068, 0069): shared, "Summit Homes left 6 comments", "Version 4 is ready", "Summit Homes approved v3". */
        design: z.object({ setId: z.uuid(), setName: z.string(), who: z.string(), count: z.number().int(), version: z.number().int().nullable().optional() }).nullable().optional(),
        /** `chapi_reply` (docs/adr/0079): Chapi's answer to an @Chapi question, for everyone in the conversation. */
        chapi: ChapiChatReply.nullable().optional(),
        /** `rule_post` (docs/adr/0109): posted as "Chapsi (rule)"; the text is the message body. */
        rule: z.object({ id: z.uuid(), name: z.string() }).nullable().optional(),
    })
        .nullable(),
    /** Imported history: who wrote it when they are not (or not yet) a Chapsi member. */
    imported: z.object({ provider: z.enum(['slack', 'legacy', 'jira', 'asana', 'clickup', 'trello', 'monday']), authorName: z.string() }).nullable(),
    mentionsMe: z.boolean(),
    /** Times written in the body, saved as exact moments when sent (docs/adr/0073). Absent on old messages. */
    times: TimeMentions.optional(),
    editedAt: z.string().nullable(),
    deleted: z.boolean(),
    clientMsgId: z.string().nullable(),
    createdAt: z.string(),
});
/** A conversation: the first message and every reply that leads back to it, oldest first. */
export const Conversation = z.object({
    root: Message,
    replies: z.array(Message),
    /** The reader muted reply notifications for this conversation. */
    muted: z.boolean(),
});
export const MuteConversationInput = z.object({ muted: z.boolean() });
export const MessagePage = z.object({
    items: z.array(Message),
    /** Oldest seq in this page; pass as `before` to load older. Null when there is nothing older. */
    nextBefore: z.number().int().nullable(),
    lastReadSeq: z.number().int(),
});
export const MessageQuery = z.object({
    before: z.coerce.number().int().optional(),
    after: z.coerce.number().int().optional(),
    projectId: z.uuid().optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
});
export const PostMessageInput = z
    .object({
    body: z.string().trim().max(10000),
    projectId: z.uuid().nullable().optional(),
    /** Reply to this message: it is quoted, and the reply posts in the channel. */
    replyToMessageId: z.uuid().nullable().optional(),
    /** Client-generated id so a retried send never double-posts. */
    clientMsgId: z.string().min(8).max(64),
    /** Completed uploads to attach (docs/adr/0014-file-uploads.md). */
    fileIds: z.array(z.uuid()).max(MAX_FILES_PER_MESSAGE).optional(),
    /** A GIF from the picker (GIPHY id); the server looks it up again before posting. */
    gifId: z.string().regex(GIPHY_ID).optional(),
    /** Times found in the body, with their exact moments (docs/adr/0073). */
    times: TimeMentions.optional(),
})
    .refine((v) => v.body.length > 0 || (v.fileIds?.length ?? 0) > 0 || !!v.gifId, { message: 'Write a message or attach a file', path: ['body'] });
export const EditMessageInput = z.object({ body: z.string().trim().min(1).max(10000), times: TimeMentions.optional() });
export const ReactInput = z.object({ emoji: z.string().min(1).max(16) });
export const MarkReadInput = z.object({ seq: z.number().int().min(0) });
export const PinInput = z.object({ label: z.string().trim().min(1).max(60) });
/** Lowercase letters, numbers and single hyphens; up to 80 characters; unique in the workspace. */
export const CHANNEL_NAME = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const CHANNEL_NAME_MAX = 80;
/** Turns what someone typed into a valid channel name: "Simonds Website!" → "simonds-website". */
export function channelNameFrom(input) {
    return input
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[\s_]+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
        .replace(/-+/g, '-')
        .replace(/^-/, '')
        .slice(0, CHANNEL_NAME_MAX);
}
/** What the create flow calls each kind. Internal channels are stored as kind 'team'. */
export const ChannelType = z.enum(['client', 'internal', 'project']);
export const CreateChannelInput = z
    .object({
    name: z.string().max(CHANNEL_NAME_MAX).regex(CHANNEL_NAME, 'Use lowercase letters, numbers and hyphens'),
    type: ChannelType,
    /** Required for client and project channels. */
    clientId: z.uuid().nullable().optional(),
    /** Required for project channels; must belong to the client. */
    projectId: z.uuid().nullable().optional(),
    private: z.boolean(),
    topic: z.string().max(250).optional(),
    /** Team members to add. The creator is always added. */
    memberIds: z.array(z.uuid()).max(500).optional(),
    /** Client contacts to invite as guests (client channels only). */
    guestEmails: z.array(z.email().max(254)).max(50).optional(),
})
    .refine((v) => v.type === 'internal' || !!v.clientId, { message: 'Choose a client', path: ['clientId'] })
    .refine((v) => v.type !== 'project' || !!v.projectId, { message: 'Choose a project', path: ['projectId'] })
    .refine((v) => v.type === 'client' || !v.guestEmails?.length, { message: 'Only client channels have guests', path: ['guestEmails'] });
export const ArchiveChannelInput = z.object({ archived: z.boolean() });
/** At most this many addresses from outside the workspace per request (docs/adr/0085). */
export const MAX_EXTERNAL_INVITES = 20;
const OptionalName = z.union([z.literal(''), z.string().max(CHANNEL_NAME_MAX).regex(CHANNEL_NAME, 'Use lowercase letters, numbers and hyphens')]);
/**
 * The New conversation modal (UI-21, docs/adr/0085): a channel (named, public or private) or a group chat (name
 * optional, invited people only), internal or external, optionally filed under a client or a project. External, and any
 * address from outside the workspace, needs owners or admins (`conversation.external`).
 */
export const CreateConversationInput = z
    .object({
    type: z.enum(['channel', 'group']),
    name: OptionalName.optional(),
    audience: z.enum(['internal', 'external']),
    /** Workspace people to add. The creator is always in it. */
    memberIds: z.array(z.uuid()).max(100).optional(),
    /** People from outside the workspace: invited by email as guests who see only this conversation. */
    externalEmails: z.array(z.email().max(254)).max(MAX_EXTERNAL_INVITES).optional(),
    linkClientId: z.uuid().nullable().optional(),
    linkProjectId: z.uuid().nullable().optional(),
    /** Channels only: invited people only. Group chats are always invited people only. */
    private: z.boolean().optional(),
    topic: z.string().max(250).optional(),
})
    .refine((v) => v.type === 'group' || !!v.name, { message: 'Give the channel a name', path: ['name'] })
    .refine((v) => v.audience === 'external' || !v.externalEmails?.length, { message: 'Choose External to invite people from other companies', path: ['externalEmails'] })
    .refine((v) => !(v.linkClientId && v.linkProjectId), { message: 'Link a client or a project, not both', path: ['linkProjectId'] });
/** Adds workspace people, and (owners and admins) invites people from outside by email, to a conversation. */
export const InviteToConversationInput = z
    .object({
    userIds: z.array(z.uuid()).max(100).optional(),
    emails: z.array(z.email().max(254)).max(MAX_EXTERNAL_INVITES).optional(),
})
    .refine((v) => (v.userIds?.length ?? 0) + (v.emails?.length ?? 0) > 0, { message: 'Add someone', path: ['userIds'] });
export const StarConversationInput = z.object({ starred: z.boolean() });
/** The Starred section's order: every starred conversation, each once. Unknown or unreadable ids are dropped. */
export const StarredConversationsInput = z.object({ channelIds: z.array(z.uuid()).max(200) });
export const StarredConversations = z.object({ channelIds: z.array(z.uuid()) });
/** Owners and admins: let Chapi read this channel, or keep it out. */
export const ChannelChapiInput = z.object({ enabled: z.boolean() });
export const OpenDmInput = z.object({ userId: z.uuid() });
export const ChannelMembersInput = z.object({ add: z.array(z.uuid()).max(100).optional(), remove: z.array(z.uuid()).max(100).optional() });
export const ChannelPrefsInput = z.object({ notify: z.enum(['all', 'mentions', 'none']) });
export const GlanceItem = WorkItemCard.extend({
    statusId: z.uuid(),
    commentCount: z.number().int(),
    slaDueAt: z.string().nullable(),
    done: z.boolean(),
});
/** Right-hand panel: the client or project at a glance. */
export const Glance = z.object({
    title: z.string(),
    progress: z.object({ done: z.number().int(), review: z.number().int(), active: z.number().int(), todo: z.number().int(), total: z.number().int() }),
    projects: z.array(z.object({ id: z.uuid(), name: z.string(), progress: z.number() })),
    tasks: z.object({ mine: z.array(GlanceItem), team: z.array(GlanceItem), open: z.number().int() }),
    support: z.object({ items: z.array(GlanceItem), open: z.number().int(), slaHours: z.number().nullable() }),
    hours: z
        .object({ month: z.string(), logged: z.number(), planned: z.number().nullable(), resetsOn: z.string().nullable() })
        .nullable(),
});
/** Realtime events sent over the WebSocket. Ids only where the client can refetch. */
export const RealtimeEvent = z.discriminatedUnion('type', [
    z.object({ type: z.literal('message.created'), channelId: z.uuid(), message: Message }),
    z.object({ type: z.literal('message.updated'), channelId: z.uuid(), message: Message }),
    z.object({ type: z.literal('work_item.changed'), workItemId: z.uuid(), projectId: z.uuid().nullable() }),
    z.object({ type: z.literal('channel.read'), channelId: z.uuid(), seq: z.number().int() }),
    z.object({ type: z.literal('sidebar.changed') }),
    /** A channel's header changed (pins): refetch the channel. Sent to its members. Ids only. */
    z.object({ type: z.literal('channel.changed'), channelId: z.uuid() }),
    /** The workspace's features changed: refetch who-am-I (features) and settings. Ids only. */
    z.object({ type: z.literal('workspace.changed') }),
    /** Sent only to the person whose inbox changed. `notification` is set when one was just created. */
    z.object({ type: z.literal('inbox.changed'), unread: z.number().int(), notification: Notification.optional() }),
    z.object({ type: z.literal('presence'), userId: z.uuid(), online: z.boolean() }),
    /**
     * Sent to one socket as it connects: everyone online right now whom this person may see (the visibility
     * rule in people/visibility.ts), never anyone set to "appear offline". Replaces the client's online list.
     */
    z.object({ type: z.literal('presence.snapshot'), online: z.array(z.uuid()) }),
    /** Someone chose busy or away, or paused or resumed notifications (docs/adr/0048). Never "appear offline". */
    z.object({ type: z.literal('availability.changed'), availability: PersonAvailability }),
    /** Sent only to the caller: their own availability changed (another tab, the phone, a pause ending). */
    z.object({ type: z.literal('my_availability.changed'), mine: MyAvailability }),
    /**
     * Sent only to the recipient: something worth a desktop alert that has no inbox row (a direct message).
     * Title rendered by the server, ids only.
     */
    z.object({ type: z.literal('alert'), kind: z.string(), title: z.string(), channelId: z.uuid().nullable(), messageId: z.uuid().nullable() }),
    /** Someone set or cleared their status. `status` is null when cleared. */
    z.object({ type: z.literal('status.changed'), userId: z.uuid(), status: UserStatus.nullable() }),
    /** Someone's profile changed (title, phone, time zone, working hours): refetch it. Ids only. */
    z.object({ type: z.literal('profile.changed'), userId: z.uuid() }),
    /** Today's birthdays changed (docs/adr/0047): refetch the card. Sent to the team only. */
    z.object({ type: z.literal('birthdays.changed') }),
    z.object({ type: z.literal('typing'), channelId: z.uuid(), userId: z.uuid() }),
    /** Sent only to the person asking Chapi: the next piece of an answer, then the finished message. */
    z.object({ type: z.literal('chapi.delta'), conversationId: z.uuid(), messageId: z.uuid(), text: z.string() }),
    z.object({ type: z.literal('chapi.done'), conversationId: z.uuid(), message: ChapiMessage }),
    /**
     * Sent only to the person concerned (docs/adr/0063): their task breakdown finished drafting, their "looks done"
     * suggestions changed, or a client update they can review changed. Refetch; ids only.
     */
    z.object({ type: z.literal('chapi.changed'), what: z.enum(['breakdown', 'done_suggestions', 'client_updates']), id: z.uuid().nullable() }),
    /**
     * Sent only to the person who asked @Chapi (docs/adr/0079): their answer in a conversation started, finished or
     * changed (an action confirmed). A public answer also arrives as a chat message for everyone.
     */
    z.object({ type: z.literal('chapi.chat_reply'), channelId: z.uuid(), reply: ChapiChatReply }),
    /** Sent only to a message's author: Chapi spotted a task in it, or a suggestion was resolved. Refetch; ids only. */
    z.object({ type: z.literal('chapi.task_suggestions'), channelId: z.uuid() }),
    /** Sent only to the person whose My day changed (docs/adr/0029-my-day.md). Ids only. */
    z.object({ type: z.literal('my_day.changed') }),
    /**
     * A channel's catch-up started, changed (someone joined, left, muted, shared) or ended (`catchUp` null).
     * Sent to the people who can read the channel (docs/adr/0057).
     */
    z.object({ type: z.literal('catch_up.changed'), channelId: z.uuid(), catchUp: CatchUp.nullable() }),
    /** Sent only to the person a signal is for, relayed from another person in the same catch-up. */
    z.object({ type: z.literal('catch_up.signal'), catchUpId: z.uuid(), fromUserId: z.uuid(), fromConnId: ConnId, toConnId: ConnId, signal: CatchUpSignal }),
    /** Someone joined or left any catch-up: "In a catch-up" on their status. Sent to the people who can see them. */
    z.object({ type: z.literal('catch_up.presence'), userId: z.uuid(), inCatchUp: z.boolean() }),
]);
//# sourceMappingURL=chat.js.map